import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import useHistoryState from 'use-history-state';
import { Location } from 'history';
import client from 'lib/CommonApi/client';
import stringUtils from 'common/stringUtils';
import { callAlertWeb, callNativeWeb, footerShowEventWeb, hideProgressWeb, showProgressWeb } from 'common/appInterface';
import CardApplyStopModal from 'components/CardApplyStopModal';

const MobileVerifyPage: React.FC = () => {
  /** Location State */
  const location = useLocation() as Location<LocationState>;
  /** Navigate */
  const navigate = useNavigate();
  /** 다국어처리 */
  const { t, i18n } = useTranslation();

  const screenId = "APPL06";
  const screenName = t("APPL06.title");

  const [cardApply, setCardApply] = useState<Partial<CardApplyStep3>>({ ...location.state.apply });
  const [verifyName, setVerifyName] = useHistoryState(location.state.apply.foreignerDivisionCode === "2" ? location.state.apply.userEngName : "", "verifyName");
  const [verifyPhoneNo, setVerifyPhoneNo] = useHistoryState(location.state.apply.phoneNo?.replace(/[^0-9]/ig, ''), "verifyPhoneNo");
  const [isClick, setClick] = useState(false);

  useEffect(() => {
    setCardApply(value => ({ ...value, verifyName: verifyName }));
  }, [verifyName]);

  useEffect(() => {
    setCardApply(value => ({ ...value, phoneNo: verifyPhoneNo }));
  }, [verifyPhoneNo]);

  const [isStop, setStop] = useState(false);
  const closeStop = useCallback(() => setStop(false), []);

  /** 뒤로가기 처리 (backKeyEvent_App (APP > WEB)) */
  const callbackBackkeyEvent = useCallback(() => {
    setStop(true);
  }, []);

  /** 인증번호 요청 */
  const clickVerify = useCallback(() => {
    if (isClick) {
      console.log("Already Clicked...");
      return;
    }

    setClick(true);
    showProgressWeb();
    client.patch("/api/app/apply/verify", { ...location.state.query, receiveSeq: cardApply.receiveSeq, receiveDate: cardApply.receiveDate, phoneNo: cardApply.phoneNo, verifyName: cardApply.verifyName }).then((response) => {
      let result = response.data.response;
      cardApply.phoneNo = verifyPhoneNo;
      navigate("/apply/step3/confirm", { state: { ...location.state, apply: { ...cardApply, kcbCertificationYn: result.kcbCertificationYn, certificationProcessId: result.certificationProcessId }}});
    }).catch((e: AxiosError) => {
      callAlertWeb("ERROR", "99", e.message);
    }).finally(() => {
      hideProgressWeb();
      setClick(false);
    });
  }, [isClick, location.state, cardApply, verifyPhoneNo, navigate]);

  // 최초실행
  useEffect(() => {
    // 언어설정
    i18n.changeLanguage(location.state.query.languageCode);
    // callNative_Web 처리 (WEB > APP)
    callNativeWeb(screenId, screenName);
    // 하단바 숨기기
    footerShowEventWeb("N");

    console.log("QUERY STATE");
    console.log(location.state);
    console.log(cardApply);

    // 최초한번실행 useEffect() Warning 제거
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 뒤로가기 이벤트 리스너 (backKeyEvent_App (APP > WEB))
  useEffect(() => {
    document.addEventListener("backpress", callbackBackkeyEvent);
    return () => document.removeEventListener("backpress", callbackBackkeyEvent);
  }, [callbackBackkeyEvent]);

  return (
    <div id="wrap">
      <div id="container" className="pcHidden">
        <section className="contents pb64-mb">
          <div className="singUpWrapSub">
            <div className="txtWrap">
              <p className="mainTxt">{t("APPL06.explan01")}</p>
            </div>
            <div className="conWrap">
              <div className="inputBoxTy01"> 
                <label>
                  <input placeholder=" " type="text" value={verifyName} onChange={(e) => setVerifyName(e.target.value, true)} />
                  <span>{cardApply.foreignerDivisionCode === "2" ? t("APPL06.header01") : t("APPL06.header01_2")}</span>
                  <p className="message">{cardApply.foreignerDivisionCode === "2" && t("APPL06.explan02")}</p>
                </label>
              </div>
              <div className="inputBoxTy01"> 
                <label>
                  <input placeholder=" " type="text" value={verifyPhoneNo} onChange={(e) => setVerifyPhoneNo(e.target.value?.replace(/[^0-9]/ig, ''), true)} pattern="[0-9]*" inputMode="numeric" onBlur={(e) => e.target.value.startsWith("1") ? setVerifyPhoneNo((value: string) => ("0" + value), true) : setVerifyPhoneNo((value: string) => (value), true) } />
                  <span>{t("APPL06.header02")}</span>
                </label>
              </div>
              <div className="inputBoxTy01"> 
                <label>
                  <input placeholder=" " type="text" value={cardApply.sexDivisionCode === "0" ? t("APPL06.sexFemale").toString() : t("APPL06.sexMale").toString()} readOnly />
                  <span>{t("APPL06.header03")}</span>
                </label>
              </div>
              <div className="inputBoxTy01"> 
                <label>
                  <input placeholder=" " type="text" value={stringUtils.dateFormat(cardApply.birthDate, 'yyyy/MM/dd')} readOnly />
                  <span>{t("APPL06.header04")}</span>
                </label>
              </div>
              <div className="txtWrap">
                <div className="subTxt2">
                  <p className="txtLine1">*</p>
                  <p className="txtLine2">
                    <span>{t("APPL06.explan03")}<br/><br/></span>
                    <span>{t("APPL06.explan04")}</span>
                  </p>
                  <p className="icoGroup">
                    <span><img src="/assets/images/ico/icon_dark_gray_call.png" alt="Call Icon"/></span>
                    <span>{t("APPL06.explan05")}</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
        <div className="btnArea">
          <div className="btnWrapTy01">
            <button type="button" className="btnPrimaryTy01" disabled={stringUtils.checkValueNull(cardApply.verifyName) || stringUtils.checkValueNull(cardApply.phoneNo) || isClick} onClick={clickVerify}><span>{t("APPL06.button01")}</span></button>
          </div>
        </div>
      </div>
      <CardApplyStopModal isStop={isStop} closeStop={closeStop} />
    </div>
  );
};

export default MobileVerifyPage;